import type {
  BeneficiosPagtosType,
  CalculoBreakdownFuncionario,
  ComparativoRegimesType,
  CustoAnualType,
  CustoFuncionarioResultsType,
  CustoMensalType,
  DescontosFuncionarioType,
  EncargosPagtosType,
  ProvisaoAnualType,
} from "../calculadora-custo-funcionario";
import {
  CONSTANTES_2025,
  REGIMES_EMPRESA_2025,
} from "../calculadora-custo-funcionario";

import {
  calcularINSS,
  calcularINSSBreakdown,
  calcularIRRF,
  calcularIRRFBreakdown,
} from "../../calculadora-comparativa/utils/comparativeFunctions";

const parseNumericValue = (value: string): number => {
  if (!value || typeof value !== "string") return 0;
  return (
    Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0
  );
};

const arredondarMoeda = (valor: number): number =>
  Math.round(valor * 100) / 100;

// Limite de isenção de IRRF para 2 salários mínimos (R$ 1.518 × 2 = R$ 3.036)
const LIMITE_ISENCAO_IRRF = 3036.0;

/**
 * Calcula IRRF com isenção para quem ganha até 2 salários mínimos
 */
const calcularIRRFComIsencao = (
  salario: number,
  inss: number,
  dependentes: number,
): number => {
  // Se salário bruto até R$ 3.036, isento de IRRF
  if (salario <= LIMITE_ISENCAO_IRRF) {
    return 0;
  }

  // Caso contrário, usar cálculo normal
  return calcularIRRF(salario, inss, dependentes);
};

/**
 * Breakdown IRRF com isenção para quem ganha até 2 salários mínimos
 */
const calcularIRRFBreakdownComIsencao = (
  salario: number,
  inss: number,
  dependentes: number,
) => {
  // Se salário bruto até R$ 3.036, isento de IRRF
  if (salario <= LIMITE_ISENCAO_IRRF) {
    return {
      salario,
      inss,
      dependentes,
      deducaoDependentes: dependentes * CONSTANTES_2025.VALOR_DEPENDENTE_IRRF,
      baseCalculo: Math.max(
        0,
        salario - inss - dependentes * CONSTANTES_2025.VALOR_DEPENDENTE_IRRF,
      ),
      faixaAplicada: { aliquota: 0, parcelaDeducao: 0 },
      valorIRRF: 0,
    };
  }

  // Caso contrário, usar cálculo normal
  return calcularIRRFBreakdown(salario, inss, dependentes);
};

export const calcularValeTransporte = (
  salarioBruto: number,
  valeTransporteValor: number,
) => {
  if (valeTransporteValor <= 0) {
    return {
      valorTotal: 0,
      descontoFuncionario: 0,
      custoEmpresa: 0,
      limiteSalarial: salarioBruto * 0.06,
    };
  }

  const limiteSalarial = salarioBruto * 0.06; // Máximo 6% do salário
  const descontoFuncionario = Math.min(valeTransporteValor, limiteSalarial);

  const custoEmpresa = Math.max(0, valeTransporteValor - descontoFuncionario);

  return {
    valorTotal: valeTransporteValor,
    descontoFuncionario,
    custoEmpresa,
    limiteSalarial,
  };
};

/**
 * Calcula encargos patronais baseado no regime da empresa
 */
export const calcularEncargosPatronais = (
  salarioBruto: number,
  regime: "simples" | "lucro",
): EncargosPagtosType => {
  const regimeConfig = REGIMES_EMPRESA_2025[regime];

  const inssPatronal = salarioBruto * regimeConfig.inssPatronal;
  const fgts = salarioBruto * regimeConfig.fgts;
  const salarioEducacao = salarioBruto * regimeConfig.salarioEducacao;
  const sistemaS = salarioBruto * regimeConfig.sistemaS;
  const seguroAcidente = salarioBruto * regimeConfig.seguroAcidente;

  const totalEncargos =
    inssPatronal + fgts + salarioEducacao + sistemaS + seguroAcidente;

  return {
    inssPatronal: arredondarMoeda(inssPatronal),
    fgts: arredondarMoeda(fgts),
    salarioEducacao: arredondarMoeda(salarioEducacao),
    sistemaS: arredondarMoeda(sistemaS),
    seguroAcidente: arredondarMoeda(seguroAcidente),
    totalEncargos: arredondarMoeda(totalEncargos),
  };
};

/**
 * Calcula 13º salário com encargos
 */
const calcular13Salario = (
  salarioBruto: number,
  regime: "simples" | "lucro",
) => {
  const decimoTerceiroLiquido = salarioBruto;

  const encargos13 = calcularEncargosPatronais(salarioBruto, regime);

  return {
    decimoTerceiro: arredondarMoeda(decimoTerceiroLiquido),
    decimoTerceiroEncargos: arredondarMoeda(encargos13.totalEncargos),
  };
};

/**
 * Calcula férias com 1/3 e encargos
 */
const calcularFeriasComEncargos = (
  salarioBruto: number,
  regime: "simples" | "lucro",
) => {
  const umTercoFerias = salarioBruto / 3;

  const encargosFerias = calcularEncargosPatronais(umTercoFerias, regime);

  return {
    feriasLiquidas: arredondarMoeda(umTercoFerias),
    feriasEncargos: arredondarMoeda(encargosFerias.totalEncargos),
  };
};

/**
 * Calcula FGTS adicional (apenas sobre 13º + 1/3 férias) e provisão para multa rescisória
 * FGTS mensal já está incluído nos encargos patronais
 */
const calcularFGTSAdicionalEMulta = (salarioBruto: number) => {
  // FGTS sobre 13º salário
  const fgts13 = salarioBruto * CONSTANTES_2025.FGTS_ALIQUOTA;

  // FGTS sobre 1/3 de férias
  const umTercoFerias = salarioBruto / 3;
  const fgtsFerias = umTercoFerias * CONSTANTES_2025.FGTS_ALIQUOTA;

  // FGTS adicional (apenas 13º + férias, mensal já contabilizado)
  const fgtsAdicional = fgts13 + fgtsFerias;

  // Para a multa, consideramos FGTS total anual: 12 meses + 13º + férias
  const fgts12Meses = salarioBruto * 12 * CONSTANTES_2025.FGTS_ALIQUOTA;
  const fgtsAnualTotal = fgts12Meses + fgts13 + fgtsFerias;
  const provisaoMultaFGTS = fgtsAnualTotal * 0.4;

  return {
    fgtsAdicional: arredondarMoeda(fgtsAdicional),
    fgtsAnualTotal: arredondarMoeda(fgtsAnualTotal),
    provisaoMultaFGTS: arredondarMoeda(provisaoMultaFGTS),
  };
};

/**
 * Calcula custo mensal completo com provisões mensais obrigatórias
 */
export const calcularCustoMensal = (
  salarioBruto: number,
  valeTransporte: number,
  valeAlimentacao: number,
  planoSaude: number,
  outrosBeneficios: number,
  regime: "simples" | "lucro",
): CustoMensalType => {
  // Descontos do funcionário
  const inss = calcularINSS(salarioBruto);
  const irrf = calcularIRRFComIsencao(salarioBruto, inss, 0); // Usando função com isenção
  const valeTransporteCalc = calcularValeTransporte(
    salarioBruto,
    valeTransporte,
  );

  const descontosFuncionario: DescontosFuncionarioType = {
    inss: arredondarMoeda(inss),
    irrf: arredondarMoeda(irrf),
    valeTransporte: arredondarMoeda(valeTransporteCalc.descontoFuncionario),
    totalDescontos: arredondarMoeda(
      inss + irrf + valeTransporteCalc.descontoFuncionario,
    ),
  };

  // Salário líquido efetivo inclui benefícios não tributáveis
  const salarioLiquidoBase = salarioBruto - descontosFuncionario.totalDescontos;
  const beneficiosNaoTributaveis =
    valeAlimentacao + planoSaude + outrosBeneficios;
  const salarioLiquidoEfetivo = salarioLiquidoBase + beneficiosNaoTributaveis;

  // Encargos patronais mensais (já incluem FGTS mensal)
  const encargosPagos = calcularEncargosPatronais(salarioBruto, regime);

  // Benefícios pagos pela empresa
  const beneficiosPagos: BeneficiosPagtosType = {
    valeTransporteEmpresa: arredondarMoeda(valeTransporteCalc.custoEmpresa),
    valeAlimentacao: arredondarMoeda(valeAlimentacao),
    planoSaude: arredondarMoeda(planoSaude),
    outrosBeneficios: arredondarMoeda(outrosBeneficios),
    totalBeneficios: arredondarMoeda(
      valeTransporteCalc.custoEmpresa +
        valeAlimentacao +
        planoSaude +
        outrosBeneficios,
    ),
  };

  // Calcular provisões mensais obrigatórias
  const calc13 = calcular13Salario(salarioBruto, regime);
  const calcFerias = calcularFeriasComEncargos(salarioBruto, regime);
  const calcFGTS = calcularFGTSAdicionalEMulta(salarioBruto);

  // Rateio das provisões anuais em base mensal
  const provisaoMensal13 =
    (calc13.decimoTerceiro + calc13.decimoTerceiroEncargos) / 12;
  const provisaoMensalFerias =
    (calcFerias.feriasLiquidas + calcFerias.feriasEncargos) / 12;
  const provisaoMensalFGTSAdicional = calcFGTS.fgtsAdicional / 12; // CORREÇÃO: inclui FGTS adicional
  const provisaoMensalMultaFGTS = calcFGTS.provisaoMultaFGTS / 12;

  const totalProvisoesMensais =
    provisaoMensal13 +
    provisaoMensalFerias +
    provisaoMensalFGTSAdicional +
    provisaoMensalMultaFGTS;

  // Custo total mensal REAL
  const custoBaseMensal =
    salarioBruto +
    encargosPagos.totalEncargos +
    beneficiosPagos.totalBeneficios;
  const custoTotalMensal = custoBaseMensal + totalProvisoesMensais;

  return {
    salarioBruto: arredondarMoeda(salarioBruto),
    salarioLiquido: arredondarMoeda(salarioLiquidoEfetivo),
    descontosFuncionario,
    encargosPagos,
    beneficiosPagos,
    custoTotalMensal: arredondarMoeda(custoTotalMensal),
  };
};

/**
 * Calcula custo anual completo
 */
export const calcularCustoAnual = (
  custoMensal: CustoMensalType,
  regime: "simples" | "lucro",
): CustoAnualType => {
  // CORREÇÃO: Custo anual é simplesmente o mensal × 12, pois o mensal já inclui todas as provisões rateadas
  const custoTotalAnual = custoMensal.custoTotalMensal * 12;

  const calc13 = calcular13Salario(custoMensal.salarioBruto, regime);
  const calcFerias = calcularFeriasComEncargos(
    custoMensal.salarioBruto,
    regime,
  );
  const calcFGTS = calcularFGTSAdicionalEMulta(custoMensal.salarioBruto);

  const provisoes: ProvisaoAnualType = {
    decimoTerceiro: calc13.decimoTerceiro,
    decimoTerceiroEncargos: calc13.decimoTerceiroEncargos,
    feriasLiquidas: calcFerias.feriasLiquidas,
    feriasEncargos: calcFerias.feriasEncargos,
    fgtsAnual: calcFGTS.fgtsAnualTotal,
    provisaoMultaFGTS: calcFGTS.provisaoMultaFGTS,
    totalProvisoes: arredondarMoeda(
      calc13.decimoTerceiro +
        calc13.decimoTerceiroEncargos +
        calcFerias.feriasLiquidas +
        calcFerias.feriasEncargos +
        calcFGTS.fgtsAnualTotal +
        calcFGTS.provisaoMultaFGTS,
    ),
  };

  const beneficiosAnuais = custoMensal.beneficiosPagos.totalBeneficios * 12;

  return {
    custoMensal12x: arredondarMoeda(custoTotalAnual),
    provisoes,
    beneficiosAnuais: arredondarMoeda(beneficiosAnuais),
    custoTotalAnual: arredondarMoeda(custoTotalAnual), // CORREÇÃO: não duplica provisões
  };
};

/**
 * Gera comparativo entre regimes - CORRIGIDO
 */
export const gerarComparativo = (
  custoRegimeAtual: CustoAnualType,
  regimeAtual: "simples" | "lucro",
  salarioBruto: number,
  valeTransporte: number,
  valeAlimentacao: number,
  planoSaude: number,
  outrosBeneficios: number,
): ComparativoRegimesType => {
  const regimeComparacao: "simples" | "lucro" =
    regimeAtual === "simples" ? "lucro" : "simples";

  const custoMensalComparacao = calcularCustoMensal(
    salarioBruto,
    valeTransporte,
    valeAlimentacao,
    planoSaude,
    outrosBeneficios,
    regimeComparacao,
  );
  const custoAnualComparacao = calcularCustoAnual(
    custoMensalComparacao,
    regimeComparacao,
  );

  // CORREÇÃO: Calcular diferença sempre como (regime comparação - regime atual)
  // Se positivo = regime atual é mais barato (economia)
  // Se negativo = regime atual é mais caro (custo adicional)

  const diferenciaMensal =
    (custoAnualComparacao.custoTotalAnual - custoRegimeAtual.custoTotalAnual) /
    12;
  const diferencaAnual =
    custoAnualComparacao.custoTotalAnual - custoRegimeAtual.custoTotalAnual;
  const diferencaPercentual =
    (custoAnualComparacao.custoTotalAnual / custoRegimeAtual.custoTotalAnual -
      1) *
    100;

  return {
    regimeAtual: REGIMES_EMPRESA_2025[regimeAtual].nome,
    regimeComparacao: REGIMES_EMPRESA_2025[regimeComparacao].nome,
    diferenciaMensal: arredondarMoeda(diferenciaMensal),
    diferencaAnual: arredondarMoeda(diferencaAnual),
    diferencaPercentual: arredondarMoeda(diferencaPercentual),
    custoAtual: arredondarMoeda(custoRegimeAtual.custoTotalAnual),
    custoComparacao: arredondarMoeda(custoAnualComparacao.custoTotalAnual),
  };
};

/**
 * Gera breakdown detalhado
 */
export const gerarBreakdown = (
  salarioBruto: number,
  valeTransporte: number,
): CalculoBreakdownFuncionario => {
  const inssBreakdown = calcularINSSBreakdown(salarioBruto);
  const irrfBreakdown = calcularIRRFBreakdownComIsencao(
    salarioBruto,
    calcularINSS(salarioBruto),
    0,
  ); // Usando função com isenção
  const valeTransporteBreakdown = calcularValeTransporte(
    salarioBruto,
    valeTransporte,
  );

  return {
    salarioBruto,
    inssBreakdown: {
      faixas: inssBreakdown.faixas,
      valorTotal: inssBreakdown.valorTotal,
    },
    irrfBreakdown: {
      baseCalculo: irrfBreakdown.baseCalculo,
      faixaAplicada: irrfBreakdown.faixaAplicada,
      valorIRRF: irrfBreakdown.valorIRRF,
    },
    valeTransporteBreakdown,
  };
};

/**
 * Validação de parâmetros
 */
export const validarParametrosCustoFuncionario = (
  salarioBruto: number,
  valeTransporte: number,
): void => {
  if (salarioBruto < CONSTANTES_2025.SALARIO_MINIMO) {
    throw new Error(
      `Salário deve ser pelo menos R$ ${CONSTANTES_2025.SALARIO_MINIMO.toFixed(2).replace(".", ",")} (salário mínimo)`,
    );
  }

  if (valeTransporte < 0) {
    throw new Error("Vale transporte não pode ser negativo");
  }
};

/**
 * Função principal do cálculo
 */
export const calcularCustoFuncionario = (
  salarioBruto: string,
  valeTransporte: string,
  valeAlimentacao: string,
  planoSaude: string,
  outrosBeneficios: string,
  regimeEmpresa: "simples" | "lucro",
): CustoFuncionarioResultsType => {
  const salarioBrutoNum = parseNumericValue(salarioBruto);
  const valeTransporteNum = parseNumericValue(valeTransporte);
  const valeAlimentacaoNum = parseNumericValue(valeAlimentacao);
  const planoSaudeNum = parseNumericValue(planoSaude);
  const outrosBeneficiosNum = parseNumericValue(outrosBeneficios);

  validarParametrosCustoFuncionario(salarioBrutoNum, valeTransporteNum);

  // Cálculo do custo mensal
  const custoMensal = calcularCustoMensal(
    salarioBrutoNum,
    valeTransporteNum,
    valeAlimentacaoNum,
    planoSaudeNum,
    outrosBeneficiosNum,
    regimeEmpresa,
  );

  // Cálculo do custo anual
  const custoAnual = calcularCustoAnual(custoMensal, regimeEmpresa);

  // Comparativo entre regimes
  const comparativo = gerarComparativo(
    custoAnual,
    regimeEmpresa,
    salarioBrutoNum,
    valeTransporteNum,
    valeAlimentacaoNum,
    planoSaudeNum,
    outrosBeneficiosNum,
  );

  // Breakdown detalhado
  const breakdown = gerarBreakdown(salarioBrutoNum, valeTransporteNum);

  return {
    custoMensal,
    custoAnual,
    comparativo,
    breakdown,
    regime: regimeEmpresa,
  };
};
